import React from "react";
import { Form, Formik, FormikConfig, FormikHelpers, FormikValues } from "formik";
import { FormikStepProps } from "./FormikStep";
import { ObjectSchema, ValidationError } from "yup";

export interface IStepsList {
    title: string;
    validationSchema: ObjectSchema<any>;
    api?: string;
    element: JSX.Element;
}
export interface IConfirmation {
    step: number;
    wasConfirmed: boolean;
    setWasConfirmed: React.Dispatch<React.SetStateAction<boolean>>;
    confirmationResponse: any;
    confirmationModalCondition: (value: any) => boolean;
    confirmationRequest: (
        _values: any,
        formikHelpers: FormikHelpers<any>
    ) => Promise<number> | void;
}
interface FormikStepperProps extends FormikConfig<FormikValues> {
    goToStep: (step: number, values?: any) => void;
    setCurrentValues?: React.Dispatch<any>;
    currentStep: number;
    validationSchema: ObjectSchema<any>;
    confirmation?: IConfirmation;
}

export default function FormikStepper({
    children,
    validationSchema,
    goToStep,
    currentStep,
    confirmation,
    setCurrentValues,
    ...props
}: FormikStepperProps) {
    const childrenArray = React.Children.toArray(
        children as React.ReactNode
    ) as React.ReactElement<FormikStepProps>[];
    const currentChild = childrenArray[currentStep];

    const isLastStep = () => {
        return currentStep === childrenArray.length - 1;
    };
    const handleNextStep = async (values: any, formikHelpers: FormikHelpers<any>) => {
        if (isLastStep()) {
            if (
                confirmation &&
                confirmation.step == currentStep &&
                !confirmation.wasConfirmed &&
                confirmation.confirmationModalCondition(values)
            ) {
                await confirmation.confirmationRequest(values, formikHelpers);
            } else {
                if (confirmation?.wasConfirmed) {
                    confirmation.setWasConfirmed(false);
                }
                try {
                    await validationSchema.validate(values, {});
                } catch (error) {
                    if (error instanceof ValidationError) {
                        const value = error.path || "";
                        childrenArray.forEach((child, index) => {
                            if (value in child.props.validationSchema.fields) {
                                goToStep(index, values);
                            }
                        });
                    }
                    return;
                }

                await props.onSubmit(values, formikHelpers);
            }
        } else {
            if (
                confirmation &&
                confirmation.step == currentStep &&
                !confirmation.wasConfirmed &&
                confirmation.confirmationModalCondition(values)
            ) {
                await confirmation.confirmationRequest(values, formikHelpers);
            } else {
                if (confirmation?.wasConfirmed) {
                    confirmation.setWasConfirmed(false);
                }
                goToStep(currentStep + 1, values);
                setCurrentValues && setCurrentValues(values);
            }
        }
    };

    return (
        <Formik
            {...props}
            validationSchema={currentChild.props.validationSchema}
            onSubmit={handleNextStep}
        >
            <Form id={`stepperForm${currentStep}`} style={{ height: "100%" }}>
                {currentChild}
            </Form>
        </Formik>
    );
}
